import { useState, useEffect, useCallback, useMemo } from 'react';
import { useActivityApi } from '../../hooks/useActivityApi.js';
import { useTagApi } from '../../hooks/useTagApi.js';

export const useAllActivitiesViewModel = () => {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [successModal, setSuccessModal] = useState(null);
  const [tagsByType, setTagsByType] = useState([]);
  const [currentTagType, setCurrentTagType] = useState(null);
  const {
    getActivities,
    deleteActivity,
    createActivity,
    updateActivity: apiUpdateActivity,
    cacheOperations: { readActivities, writeActivities }
  } = useActivityApi();

  const {
    getTags,
    getTagsByType,
    cacheOperations: { readTags, writeTags }
  } = useTagApi();


  const fetchTagsByType = useCallback(
    async (tagType) => {
      if (!tagType) {
        setTagsByType([]);
        return [];
      }

      if (currentTagType === tagType && tagsByType.length > 0) {
        return tagsByType;
      }

      try {
        const result = await getTagsByType(tagType);
        if (result.success) {
          setCurrentTagType(tagType);
          setTagsByType(result.data || []);
          return result.data;
        }
        return tagsByType;
      } catch (error) {
        console.error("Error fetching tags:", error);
        setTagsByType([]);
        return [];
      }
    },
    [getTagsByType]
  );


  const fetchActivities = useCallback(async () => {
    if (loading) return;
    setLoading(true);
    setError(null);
    try {
      const [activitiesResult, tagsResult] = await Promise.all([getActivities(), getTags()])
      if (activitiesResult.success && tagsResult.success) {
        setActivities(activitiesResult.data);
        writeActivities(activitiesResult.data)
        writeTags(tagsResult.data)
      } else {
        setError(activitiesResult.error);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [getActivities, readTags, writeTags, setActivities, readActivities, writeActivities]);


  useEffect(() => {
    fetchActivities();
  }, []);

  const handleDelete = useCallback(async (activity) => {
    try {
      const result = await deleteActivity(parseInt(activity.activity_id));
      if (result.success) {
        setSuccessModal("Activity deleted successfully");
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError(err.message);
    }
  }, [deleteActivity]);

  const handleCreateActivity = useCallback(async (formData) => {
    try {
      const result = await createActivity(formData);
      if (result.success) {

        return { success: true };
      } else {
        throw new Error(result.error);
      }
    } catch (error) {
      console.error("Error creating Activity in View Model:", error);
      throw new Error(error);
    }
  }, [createActivity]);

  const updateActivity = useCallback(async (activityId, formData) => {
    try {
      const result = await apiUpdateActivity(activityId, formData);
      if (result.success) {

        return { success: true };
      } else {
        throw new Error(result.error);
      }
    } catch (error) {
      throw new Error(error.message);
    }
  }, [apiUpdateActivity]);

  const getTagName = useMemo(
    () => (tagId) => {
      const tags = readTags();
      const tag = tags.find(
        (c) => c.tag_id.toString() === tagId?.toString()
      );
      return tag?.tag_name || "Unknown";
    },
    [readTags]
  );

  return {
    activities: readActivities(),
    error,
    handleDelete,
    createActivity: handleCreateActivity,
    updateActivity,
    tags: readTags(),
    getTagName,
    successModal,
    setSuccessModal,
    fetchTagsByType,
    tagsByType
  };
}; 